import type { IntlShape } from "react-intl";

import { Text } from "@/components/atoms/Text";
import { addOnImageUrl } from "@/lib/utils/image-utils";

import { formatUnitCostLine } from "./AddOnItemDetails.utils";
import type { PriceAddOnItemDetails } from "./utils/types";

type AddOnItemDetailsProps = {
  currency: string;
  details: PriceAddOnItemDetails;
  hideHeading?: boolean;
  /** Required — consumers own the intl instance (no `useIntl()` here). */
  intl: IntlShape;
  name?: string;
};

/**
 * Catalog add-on details (photo, category, price, description, extra charges)
 * shown inside a PriceLineItem details modal. Consumers pass `intl`; all copy is
 * formatted to strings so hosts on older react-intl majors can supply a shim.
 */
export const AddOnItemDetails = ({
  currency,
  details,
  hideHeading = true,
  intl,
  name,
}: AddOnItemDetailsProps) => {
  const categoryLine = [details.categoryDisplay, details.subcategoryDisplay]
    .filter(Boolean)
    .join(" / ");
  const unitCostLine = formatUnitCostLine({ currency, details, intl });
  const visibleCharges = (details.additionalCharges ?? []).filter(
    (charge) => charge.chargeType !== "included_vat",
  );

  return (
    <div data-testid="add-on-item-details">
      {details.imageId ? (
        <div className="tw-mb-16" data-testid="add-on-photo">
          <img
            alt={intl.formatMessage({ defaultMessage: "Add-on image" })}
            className="tw-h-full tw-w-full"
            src={addOnImageUrl(details.imageId)}
          />
        </div>
      ) : null}

      {categoryLine ? (
        <Text
          className="tw-text-gray-600"
          testId="add-on-category"
          typeface="caption-2"
        >
          {categoryLine}
        </Text>
      ) : null}

      {!hideHeading && name ? (
        <Text className="tw-mt-8 tw-mb-8" element="h4" typeface="headline-3-m">
          {name}
        </Text>
      ) : null}

      {unitCostLine ? (
        <Text className="tw-mt-8" element="div" typeface="body-1">
          {unitCostLine}
        </Text>
      ) : null}

      {details.description ? (
        <Text
          className="tw-text-gray-600 tw-mt-16 tw-mb-16"
          element="div"
          testId="add-on-description"
          typeface="body-1"
        >
          {details.description}
        </Text>
      ) : null}

      {visibleCharges.length > 0 ? (
        <div data-testid="add-on-additional-charges">
          <Text className="tw-mb-8" element="p" typeface="body-1-m">
            {intl.formatMessage({ defaultMessage: "Taxes & Fees" })}
          </Text>
          {visibleCharges.map((charge) => (
            <div
              className="tw-flex tw-justify-between tw-mb-12"
              data-testid="additional-charge"
              key={charge.id}
            >
              <Text className="tw-text-gray-600" typeface="body-1">
                {charge.label}
              </Text>
              <Text
                className="tw-text-gray-600 tw-text-right"
                typeface="body-1"
              >
                {charge.unit === "%" && charge.displayString
                  ? charge.displayString
                  : intl.formatNumber(charge.amount, {
                      style: "currency",
                      currency,
                    })}
              </Text>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
